export type AdminRecord = Record<string, unknown>;
export type AdminFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** Same-origin admin API client. Keeps the CSRF token in memory only. */
export class AdminClient {
  private csrf = '';

  constructor(private readonly fetcher: AdminFetch = fetch) {}

  async session(): Promise<AdminRecord> {
    const result = await this.get('session');
    this.csrf = String(result.csrf ?? '');
    return result;
  }

  async login(username: string, password: string): Promise<AdminRecord> {
    const result = await this.post('login', { username, password });
    this.csrf = String(result.csrf ?? '');
    return result;
  }

  async logout(): Promise<void> {
    try {
      await this.post('logout');
    } finally {
      this.csrf = '';
    }
  }

  users(): Promise<AdminRecord> { return this.get('users'); }
  rooms(): Promise<AdminRecord> { return this.get('rooms'); }
  audit(): Promise<AdminRecord> { return this.get('audit'); }
  ticket(room: string): Promise<AdminRecord> { return this.post('ticket', { room }); }

  private get(action: string): Promise<AdminRecord> {
    return this.request(`/api/admin?action=${encodeURIComponent(action)}`, { method: 'GET', credentials: 'same-origin' });
  }

  private post(action: string, body: AdminRecord = {}): Promise<AdminRecord> {
    return this.request('/api/admin', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(this.csrf ? { 'X-CSRF-Token': this.csrf } : {}) },
      body: JSON.stringify({ action, ...body }),
    });
  }

  private async request(input: string, init: RequestInit): Promise<AdminRecord> {
    const response = await this.fetcher(input, init);
    let data: AdminRecord = {};
    try { data = await response.json() as AdminRecord; } catch { /* normalize non-JSON failures */ }
    if (!response.ok) throw new Error(String(data.message ?? `İstek başarısız (${response.status})`));
    return data;
  }
}
